import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import SearchBar from "./SearchBar";
import Notificaciones from "./Notificaciones";
import { updateProfile } from "firebase/auth";
import FullScreen from "./FullScreen";
import { useAuth } from "../context/AuthContext";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { storage } from "../firebaseConfig";
import { useNavContext } from "../context/NavContext";
import ProfileMenu from "./ProfileMenu";
import AccesosRapidos from "./AccesosRapidos";
import Modal from "react-modal";

const cropModalStyles = {
  overlay: {
    backgroundColor: "rgba(0,0,0,0.5)",
    zIndex: 1005, // por encima del sidebar (1003)
  },
  content: {
    inset: "50% auto auto 50%",
    transform: "translate(-50%, -50%)",
    border: "none",
    borderRadius: "6px",
    padding: "20px",
    width: "min(90vw, 420px)",
    maxHeight: "90vh",
    overflowX: "hidden",
  },
};

const DEFAULT_AVATAR = `${process.env.PUBLIC_URL}/img/avatar2.jpg`;

const resizeImage = (file) => {
  // Tu función de redimensionamiento permanece igual
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;

      img.onload = () => {
        const canvas = document.createElement("canvas");
        const maxWidth = 800;
        const maxHeight = 800;

        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > maxWidth) {
            height *= maxWidth / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width *= maxHeight / height;
            height = maxHeight;
          }
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error("Error al redimensionar la imagen"));
            }
          },
          "image/jpeg",
          0.7
        );
      };

      img.onerror = () => {
        reject(new Error("Error al cargar la imagen"));
      };
    };
    reader.onerror = (error) => reject(error);
  });
};

const Nav = () => {
  const { setSearchTerm, proximasAVencer, proximosEventos } = useNavContext(); // Consumir el contexto

  const {
    user,
    logout,
    userRole,
    loading,
    avatarUrl,
    firstName,
    lastName,
    setAvatarUrl,
  } = useAuth();

  const capitalizar = (str = "") =>
    str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  const shortFirst = capitalizar(firstName?.trim().split(" ")[0] ?? "");
  const shortLast = capitalizar(lastName?.trim().split(" ")[0] ?? "");

  const avatarSrc =
    avatarUrl && avatarUrl.trim() !== "" ? avatarUrl : DEFAULT_AVATAR;

  const [menuVisible, setMenuVisible] = useState(false);
  const menuContainerRef = useRef(null);
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [crop, setCrop] = useState({ x: 50, y: 50, width: 300, height: 300 });
  const [uploading, setUploading] = useState(false);
  const cropBoxRef = useRef(null);
  const imgWrapRef = useRef(null);

  const navigate = useNavigate();

  useEffect(() => {
    if (!menuVisible) return;

    const handleClickOutside = (e) => {
      if (
        menuContainerRef.current &&
        !menuContainerRef.current.contains(e.target)
      ) {
        setMenuVisible(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuVisible]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file || !file.type.startsWith("image/")) {
      return Swal.fire({
        icon: "error",
        title: "Archivo no válido",
        text: "Por favor, selecciona un archivo de imagen.",
        confirmButtonColor: "#007bff",
      });
    }
    setSelectedFile(file);
    setCropModalOpen(true);
  };

  const handleCropMouseDown = (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startY = e.clientY;
    const initialCrop = { ...crop };
    const bounds = imgWrapRef.current?.getBoundingClientRect();

    const handleMouseMove = (moveEvent) => {
      const diffX = moveEvent.clientX - startX;
      const diffY = moveEvent.clientY - startY;

      setCrop((prev) => {
        const maxX = Math.max(0, (bounds?.width ?? prev.width) - prev.width);
        const maxY = Math.max(0, (bounds?.height ?? prev.height) - prev.height);
        return {
          ...prev,
          x: Math.min(Math.max(0, initialCrop.x + diffX), maxX),
          y: Math.min(Math.max(0, initialCrop.y + diffY), maxY),
        };
      });
    };

    const handleMouseUp = () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const handleConfirmImage = async () => {
    if (!selectedFile) return;
    setUploading(true);
    try {
      const blob = await resizeImage(selectedFile);
      const storageRef = ref(
        storage,
        `avatars/${user.uid}/${selectedFile.name}`
      );
      const uploadTask = uploadBytesResumable(storageRef, blob);
      uploadTask.on(
        "state_changed",
        null,
        (err) => {
          console.error(err);
          setUploading(false);
          Swal.fire({
            icon: "error",
            title: "Error al subir la imagen",
            confirmButtonColor: "#007bff",
          });
        },
        async () => {
          const url = await getDownloadURL(uploadTask.snapshot.ref);
          await updateProfile(user, { photoURL: url });
          await setAvatarUrl(url);
          setCropModalOpen(false);
          setUploading(false);
          Swal.fire({
            icon: "success",
            title: "Imagen subida correctamente",
            confirmButtonColor: "#007bff",
          });
        }
      );
    } catch (err) {
      console.error(err);
      setUploading(false);
      Swal.fire({
        icon: "error",
        title: "Error al procesar la imagen",
        confirmButtonColor: "#007bff",
      });
    }
  };

  if (loading) return null;

  return (
    <div
      style={{
        position: "sticky",
        top: 0,
        zIndex: 102,
        padding: "10px",
        background: "var(--nav-bg)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "10px 5px",
          gap: "20px",
          background: "var(--nav-container)",
          border: "1px solid var(--nav-border)",
          borderRadius: "16px",
          boxShadow: "var(--nav-shadow)",
          marginLeft: "calc(var(--sidebar-extra) + 16px)",
          marginRight: "4px",
          color: "var(--nav-text)",
        }}
      >
        {/* IZQUIERDA - Buscador extendido */}
        <div
          style={{
            flex: 1,
            padding: "10px",
            transition: "padding-left 0.25s ease",
          }}
        >
          <SearchBar handleSearch={setSearchTerm} />
        </div>

        {/* DERECHA - Iconos + perfil */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px", // Reducimos un poco el gap general
            height: "100%",
          }}
        >
          <AccesosRapidos />

          <FullScreen />

          <Notificaciones
            proximasAVencer={proximasAVencer}
            proximosEventos={proximosEventos}
          />

          {/* ────────── Perfil + menú ────────── */}
          <div ref={menuContainerRef} style={{ position: "relative" }}>
            {/* visible en la top-bar */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                borderLeft: "1px solid var(--nav-border)",
                paddingInline: 20,
                gap: 12,
              }}
            >
              {/* nombre y rol */}
              <div style={{ textAlign: "left", lineHeight: 1.1 }}>
                <span
                  style={{
                    fontWeight: 600,
                    fontSize: 14,
                    color: "var(--nav-text)",
                  }}
                >
                  {shortFirst} {shortLast}
                </span>
                <br />
                <span style={{ fontSize: 12, color: "var(--nav-muted)" }}>
                  {userRole}
                </span>
              </div>

              {/* avatar (ya no clickable) + chevron */}
              <div style={{ position: "relative", width: 45, height: 45 }}>
                <img
                  src={avatarSrc}
                  alt="Avatar"
                  style={{
                    width: "100%",
                    height: "100%",
                    borderRadius: "50%",
                    objectFit: "cover",
                    pointerEvents: "none",
                  }}
                />

                {/* botón chevron que abre / cierra el menú */}
                <span
                  onClick={() => setMenuVisible((v) => !v)}
                  style={{
                    position: "absolute",
                    right: -4,
                    bottom: -4,
                    width: 18,
                    height: 18,
                    borderRadius: "50%",
                    background: "var(--nav-avatar-bg)",
                    boxShadow: "0 0 0 1px var(--nav-border)",
                    color: "var(--nav-text)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                  }}
                >
                  <i
                    className={`bi bi-chevron-${menuVisible ? "up" : "down"}`}
                    style={{ fontSize: 12 }}
                  />
                </span>
              </div>
            </div>

            {/* dropdown tipo “Natalia Taylor” */}
            {menuVisible && (
              <div style={{ position: "absolute", top: 56, right: 0 }}>
                <ProfileMenu
                  avatarSrc={avatarSrc}
                  name={`${shortFirst} ${shortLast}`}
                  emailOrRole={userRole}
                  onUploadImage={handleImageUpload}
                  onLogout={handleLogout}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <Modal
        isOpen={cropModalOpen}
        onRequestClose={() => setCropModalOpen(false)}
        style={cropModalStyles}
        shouldCloseOnOverlayClick={true}
        ariaHideApp={false}
        bodyOpenClassName="crop-open" // ← clase en <body> SOLO para este modal
      >
        <div>
          <div
            ref={imgWrapRef}
            style={{
              position: "relative",
              width: "100%",
              height: "auto",
              overflow: "hidden",
            }}
          >
            <img
              src={selectedFile ? URL.createObjectURL(selectedFile) : ""}
              alt="Cropped Preview"
              style={{
                width: "100%",
                borderRadius: "4px",
                userSelect: "none",
              }}
            />
            <div
              ref={cropBoxRef}
              style={{
                position: "absolute",
                border: "3px solid white",
                width: `${crop.width}px`,
                height: `${crop.height}px`,
                left: `${crop.x}px`,
                top: `${crop.y}px`,
                cursor: "move",
                borderRadius: "2px",
              }}
              onMouseDown={handleCropMouseDown}
            />
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: 10,
            }}
          >
            <button
              onClick={() => setCropModalOpen(false)}
              style={{
                padding: "10px 20px",
                backgroundColor: "#6c757d",
                border: "none",
                borderRadius: 5,
                color: "#fff",
                cursor: "pointer",
              }}
            >
              Cancelar
            </button>
            <button
              onClick={handleConfirmImage}
              disabled={uploading}
              style={{
                padding: "10px 20px",
                backgroundColor: uploading ? "#999" : "#007bff",
                color: "#fff",
                border: "none",
                borderRadius: 5,
                cursor: uploading ? "not-allowed" : "pointer",
              }}
            >
              {uploading ? "Subiendo..." : "OK"}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Nav;
